import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Search,
  Filter,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Eye,
  Edit3,
  ToggleLeft,
  ToggleRight,
  X,
  Copy,
  Clock,
  Zap,
  Activity,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Code2,
  Tag,
  Server,
  BarChart2,
  RefreshCw,
  Plus,
} from 'lucide-react';
import './detection-rules.scss';

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const RULES = [
  {
    id: 'dr-001',
    name: 'Multiple Failed Login Attempts',
    description:
      'Triggers when a single source IP generates more than 10 failed authentication events within a 5-minute window. Indicates potential brute-force or credential stuffing activity.',
    category: 'Identity & Access',
    severity: 'high',
    status: 'active',
    platform: 'Microsoft Sentinel',
    tactic: 'Credential Access',
    technique: 'T1110 – Brute Force',
    author: 'SOC Team',
    createdAt: '2026-01-12T09:00:00Z',
    lastTriggered: '2026-05-13T07:22:00Z',
    hitCount: 342,
    falsePositiveRate: '4%',
    logic: `SecurityEvent
| where EventID == 4625
| summarize FailedAttempts = count() by IpAddress, bin(TimeGenerated, 5m)
| where FailedAttempts > 10
| project TimeGenerated, IpAddress, FailedAttempts`,
  },
  {
    id: 'dr-002',
    name: 'Malware Detected by EDR',
    description:
      'Fires whenever an EDR platform (CrowdStrike or Defender) reports a confirmed malware detection. Mapped to the Execution tactic for immediate triage.',
    category: 'Endpoint',
    severity: 'critical',
    status: 'active',
    platform: 'CrowdStrike',
    tactic: 'Execution',
    technique: 'T1059 – Command and Scripting Interpreter',
    author: 'SOC Team',
    createdAt: '2026-01-15T10:30:00Z',
    lastTriggered: '2026-05-12T14:11:00Z',
    hitCount: 87,
    falsePositiveRate: '1%',
    logic: `index=crowdstrike sourcetype=crowdstrike:events:sensor
EventType=DetectionSummaryEvent Severity IN (3,4)
| stats count by ComputerName, FileName, SHA256HashData
| where count > 0`,
  },
  {
    id: 'dr-003',
    name: 'Suspicious Outbound DNS Query',
    description:
      'Detects DNS queries to domains flagged in threat intelligence feeds, which may indicate C2 communication or data exfiltration via DNS tunneling.',
    category: 'Network',
    severity: 'high',
    status: 'active',
    platform: 'Microsoft Sentinel',
    tactic: 'Command & Control',
    technique: 'T1071.004 – DNS',
    author: 'Network Security',
    createdAt: '2026-02-01T08:00:00Z',
    lastTriggered: '2026-05-11T22:04:00Z',
    hitCount: 215,
    falsePositiveRate: '8%',
    logic: `DnsEvents
| where QueryType == "A"
| join kind=inner (ThreatIntelligenceIndicator | where Active == true) on $left.Name == $right.DomainName
| project TimeGenerated, Computer, Name, IPAddresses, ConfidenceScore`,
  },
  {
    id: 'dr-004',
    name: 'Lateral Movement via SMB',
    description:
      'Identifies lateral movement attempts using SMB protocol by correlating authentication events across multiple internal hosts within a short time window.',
    category: 'Network',
    severity: 'critical',
    status: 'active',
    platform: 'Microsoft Sentinel',
    tactic: 'Lateral Movement',
    technique: 'T1021.002 – SMB/Windows Admin Shares',
    author: 'Threat Intelligence',
    createdAt: '2026-02-10T11:00:00Z',
    lastTriggered: '2026-05-10T03:18:00Z',
    hitCount: 29,
    falsePositiveRate: '2%',
    logic: `SecurityEvent
| where EventID in (4624, 4648) and LogonType == 3
| summarize TargetHosts = dcount(Computer) by Account, bin(TimeGenerated, 10m)
| where TargetHosts >= 3
| project TimeGenerated, Account, TargetHosts`,
  },
  {
    id: 'dr-005',
    name: 'New Admin Account Created',
    description:
      'Alerts when a new account is added to a privileged group (Domain Admins, Local Admins) outside of approved maintenance windows.',
    category: 'Identity & Access',
    severity: 'high',
    status: 'active',
    platform: 'Microsoft Sentinel',
    tactic: 'Privilege Escalation',
    technique: 'T1136 – Create Account',
    author: 'SOC Team',
    createdAt: '2026-02-14T09:00:00Z',
    lastTriggered: '2026-05-09T16:45:00Z',
    hitCount: 14,
    falsePositiveRate: '5%',
    logic: `SecurityEvent
| where EventID == 4732
| where TargetUserName has_any ("Administrators", "Domain Admins")
| project TimeGenerated, SubjectUserName, TargetUserName, MemberName`,
  },
  {
    id: 'dr-006',
    name: 'Data Exfiltration – Large Upload',
    description:
      'Triggers when a single endpoint uploads more than 500 MB to an external destination within one hour, which may indicate data theft.',
    category: 'Data Protection',
    severity: 'critical',
    status: 'active',
    platform: 'Palo Alto',
    tactic: 'Exfiltration',
    technique: 'T1048 – Exfiltration Over Alternative Protocol',
    author: 'DLP Team',
    createdAt: '2026-03-01T08:00:00Z',
    lastTriggered: '2026-05-08T11:30:00Z',
    hitCount: 6,
    falsePositiveRate: '10%',
    logic: `traffic
| summarize TotalBytes = sum(bytes_sent) by src_ip, bin(TimeGenerated, 1h)
| where TotalBytes > 524288000
| join kind=leftouter (known_destinations) on $left.dst_ip == $right.ip
| where isempty(Category)`,
  },
  {
    id: 'dr-007',
    name: 'PowerShell Encoded Command Execution',
    description:
      'Detects PowerShell invocations that use Base64-encoded command strings, commonly associated with malware loaders and living-off-the-land attacks.',
    category: 'Endpoint',
    severity: 'high',
    status: 'active',
    platform: 'Microsoft Defender',
    tactic: 'Defense Evasion',
    technique: 'T1027 – Obfuscated Files or Information',
    author: 'SOC Team',
    createdAt: '2026-03-05T10:00:00Z',
    lastTriggered: '2026-05-13T01:55:00Z',
    hitCount: 178,
    falsePositiveRate: '12%',
    logic: `DeviceProcessEvents
| where FileName =~ "powershell.exe"
| where ProcessCommandLine matches regex @"-[Ee][Nn][Cc]"
| project Timestamp, DeviceName, AccountName, ProcessCommandLine`,
  },
  {
    id: 'dr-008',
    name: 'Phishing Email – Malicious Attachment',
    description:
      'Identifies emails with executable or macro-enabled attachments from external senders that match known phishing patterns.',
    category: 'Email Security',
    severity: 'medium',
    status: 'active',
    platform: 'Microsoft Defender',
    tactic: 'Initial Access',
    technique: 'T1566.001 – Spearphishing Attachment',
    author: 'Email Security Team',
    createdAt: '2026-03-10T08:00:00Z',
    lastTriggered: '2026-05-12T09:14:00Z',
    hitCount: 523,
    falsePositiveRate: '15%',
    logic: `EmailAttachmentInfo
| where FileType in ("exe","vbs","js","doc","xls","docm","xlsm")
| join EmailEvents on NetworkMessageId
| where EmailDirection == "Inbound" and DeliveryAction == "Delivered"
| project Timestamp, RecipientEmailAddress, FileName, FileType, SenderFromAddress`,
  },
  {
    id: 'dr-009',
    name: 'Impossible Travel Login',
    description:
      'Flags user authentications from two geographically distant locations within a time window that makes physical travel impossible.',
    category: 'Identity & Access',
    severity: 'high',
    status: 'active',
    platform: 'Microsoft Sentinel',
    tactic: 'Initial Access',
    technique: 'T1078 – Valid Accounts',
    author: 'Threat Intelligence',
    createdAt: '2026-03-15T09:00:00Z',
    lastTriggered: '2026-05-10T18:33:00Z',
    hitCount: 41,
    falsePositiveRate: '7%',
    logic: `SigninLogs
| where ResultType == 0
| summarize Locations = make_set(Location), Times = make_list(TimeGenerated) by UserPrincipalName
| mv-expand Locations, Times
| order by UserPrincipalName, Times asc
| where distance(prev(Locations), Locations) > 800 and datetime_diff('hour', Times, prev(Times)) < 3`,
  },
  {
    id: 'dr-010',
    name: 'Ransomware File Extension Change',
    description:
      'Monitors for rapid mass renaming of files with extensions commonly associated with ransomware encryption (.encrypted, .locked, .crypto).',
    category: 'Endpoint',
    severity: 'critical',
    status: 'active',
    platform: 'CrowdStrike',
    tactic: 'Impact',
    technique: 'T1486 – Data Encrypted for Impact',
    author: 'SOC Team',
    createdAt: '2026-03-20T10:00:00Z',
    lastTriggered: '2026-04-28T04:02:00Z',
    hitCount: 3,
    falsePositiveRate: '0%',
    logic: `index=crowdstrike sourcetype=crowdstrike:events:sensor EventType=RansomwareDetected
| stats count by ComputerName, UserName, FilePath
| where count > 50`,
  },
  {
    id: 'dr-011',
    name: 'Cloud Storage Bucket Made Public',
    description:
      'Detects when an S3 bucket or Azure Blob storage container ACL is changed to allow public read access, which could expose sensitive data.',
    category: 'Cloud',
    severity: 'high',
    status: 'draft',
    platform: 'AWS CloudTrail',
    tactic: 'Collection',
    technique: 'T1530 – Data from Cloud Storage',
    author: 'Cloud Security',
    createdAt: '2026-04-01T09:00:00Z',
    lastTriggered: null,
    hitCount: 0,
    falsePositiveRate: '—',
    logic: `filter eventSource = "s3.amazonaws.com"
| filter eventName = "PutBucketAcl"
| filter requestParameters.accessControlList.x-amz-grant-read = "*"
| fields eventTime, userIdentity.arn, requestParameters.bucketName`,
  },
  {
    id: 'dr-012',
    name: 'IPS Signature Match – SQL Injection',
    description:
      'Fires when the IPS engine matches HTTP requests containing SQL injection payloads targeting web application databases.',
    category: 'Network',
    severity: 'medium',
    status: 'active',
    platform: 'Palo Alto',
    tactic: 'Initial Access',
    technique: 'T1190 – Exploit Public-Facing Application',
    author: 'Network Security',
    createdAt: '2026-04-05T10:00:00Z',
    lastTriggered: '2026-05-13T06:50:00Z',
    hitCount: 1204,
    falsePositiveRate: '20%',
    logic: `threat
| where category = "sql-injection"
| stats count by src_ip, dst_ip, app, threat_name
| where count > 5
| sort -count`,
  },
  {
    id: 'dr-013',
    name: 'Service Account Anomalous Login',
    description:
      'Alerts when a service account performs an interactive logon (Type 2) or logs in from an unexpected workstation.',
    category: 'Identity & Access',
    severity: 'medium',
    status: 'disabled',
    platform: 'Microsoft Sentinel',
    tactic: 'Defense Evasion',
    technique: 'T1078.002 – Domain Accounts',
    author: 'SOC Team',
    createdAt: '2026-04-10T09:00:00Z',
    lastTriggered: '2026-04-15T13:22:00Z',
    hitCount: 8,
    falsePositiveRate: '30%',
    logic: `SecurityEvent
| where EventID == 4624 and LogonType == 2
| where Account startswith "svc_"
| project TimeGenerated, Account, Computer, IpAddress`,
  },
  {
    id: 'dr-014',
    name: 'Mimikatz / Credential Dumping',
    description:
      'Detects process access patterns consistent with Mimikatz LSASS memory dumping used to harvest Windows credentials.',
    category: 'Endpoint',
    severity: 'critical',
    status: 'active',
    platform: 'Microsoft Defender',
    tactic: 'Credential Access',
    technique: 'T1003.001 – LSASS Memory',
    author: 'Threat Intelligence',
    createdAt: '2026-04-12T10:00:00Z',
    lastTriggered: '2026-05-07T02:11:00Z',
    hitCount: 5,
    falsePositiveRate: '1%',
    logic: `DeviceEvents
| where ActionType == "OpenProcessApiCall"
| where FileName =~ "lsass.exe"
| where InitiatingProcessFileName !in~ ("svchost.exe","MsMpEng.exe","csrss.exe")
| project Timestamp, DeviceName, InitiatingProcessFileName, InitiatingProcessCommandLine`,
  },
  {
    id: 'dr-015',
    name: 'Excessive Privilege Escalation Attempts',
    description:
      'Identifies accounts that repeatedly attempt to execute commands requiring elevated privileges, suggesting an active privilege escalation attempt.',
    category: 'Identity & Access',
    severity: 'medium',
    status: 'draft',
    platform: 'Microsoft Sentinel',
    tactic: 'Privilege Escalation',
    technique: 'T1548 – Abuse Elevation Control Mechanism',
    author: 'SOC Team',
    createdAt: '2026-04-20T09:00:00Z',
    lastTriggered: null,
    hitCount: 0,
    falsePositiveRate: '—',
    logic: `SecurityEvent
| where EventID in (4673, 4674)
| summarize Attempts = count() by Account, bin(TimeGenerated, 30m)
| where Attempts > 15
| project TimeGenerated, Account, Attempts`,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', cls: 'sev--critical' },
  high:     { label: 'High',     cls: 'sev--high'     },
  medium:   { label: 'Medium',   cls: 'sev--medium'   },
  low:      { label: 'Low',      cls: 'sev--low'      },
};

const STATUS_CONFIG = {
  active:   { label: 'Active',   cls: 'st--active'   },
  draft:    { label: 'Draft',    cls: 'st--draft'     },
  disabled: { label: 'Disabled', cls: 'st--disabled'  },
};

const CATEGORIES = ['All', 'Identity & Access', 'Endpoint', 'Network', 'Data Protection', 'Email Security', 'Cloud'];
const SEVERITIES = ['All', 'Critical', 'High', 'Medium', 'Low'];
const STATUSES   = ['All', 'Active', 'Draft', 'Disabled'];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SeverityBadge({ severity }) {
  const c = SEVERITY_CONFIG[severity] || { label: severity, cls: '' };
  return <span className={`dr-badge dr-sev-badge ${c.cls}`}>{c.label}</span>;
}

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || { label: status, cls: '' };
  return <span className={`dr-badge dr-st-badge ${c.cls}`}>{c.label}</span>;
}

function SeverityIcon({ severity }) {
  const colors = { critical: 'var(--status-error)', high: 'var(--status-warning)', medium: 'var(--status-info)', low: 'var(--status-success)' };
  return <ShieldAlert size={14} style={{ color: colors[severity] || 'var(--text-muted)', flexShrink: 0 }} />;
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function RuleModal({ rule, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="dr-modal-overlay" onClick={onClose}>
      <div className="dr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dr-modal__header">
          <div className="dr-modal__title-row">
            <SeverityIcon severity={rule.severity} />
            <h2 className="dr-modal__title">{rule.name}</h2>
          </div>
          <div className="dr-modal__badges">
            <SeverityBadge severity={rule.severity} />
            <StatusBadge status={rule.status} />
          </div>
          <button className="dr-modal__close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="dr-modal__body">
          <p className="dr-modal__desc">{rule.description}</p>

          <div className="dr-modal__meta-grid">
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><Tag size={11} /> Category</span>
              <span className="dr-meta-item__value">{rule.category}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><Server size={11} /> Platform</span>
              <span className="dr-meta-item__value">{rule.platform}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><Zap size={11} /> Tactic</span>
              <span className="dr-meta-item__value">{rule.tactic}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><Activity size={11} /> Technique</span>
              <span className="dr-meta-item__value">{rule.technique}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><Clock size={11} /> Last Triggered</span>
              <span className="dr-meta-item__value">{formatDate(rule.lastTriggered)}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><BarChart2 size={11} /> Total Hits</span>
              <span className="dr-meta-item__value">{rule.hitCount.toLocaleString()}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><AlertTriangle size={11} /> False Positive Rate</span>
              <span className="dr-meta-item__value">{rule.falsePositiveRate}</span>
            </div>
            <div className="dr-meta-item">
              <span className="dr-meta-item__label"><CheckCircle size={11} /> Author</span>
              <span className="dr-meta-item__value">{rule.author}</span>
            </div>
          </div>

          <div className="dr-modal__logic">
            <div className="dr-modal__logic-header">
              <Code2 size={14} />
              <span>Detection Logic</span>
              <button className="dr-copy-btn" title="Copy" onClick={() => navigator.clipboard?.writeText(rule.logic)}>
                <Copy size={12} /> Copy
              </button>
            </div>
            <pre className="dr-modal__logic-code">{rule.logic}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DetectionRules() {
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [sevFilter, setSevFilter] = useState('All');
  const [stFilter, setStFilter]   = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [rules, setRules]         = useState(RULES);

  const filtered = rules
    .filter((r) => {
      const q = search.toLowerCase();
      return (
        r.name.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q) ||
        r.platform.toLowerCase().includes(q) ||
        r.technique.toLowerCase().includes(q)
      );
    })
    .filter((r) => catFilter === 'All' || r.category === catFilter)
    .filter((r) => sevFilter === 'All' || r.severity === sevFilter.toLowerCase())
    .filter((r) => stFilter  === 'All' || r.status   === stFilter.toLowerCase())
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  const toggleStatus = (id, e) => {
    e.stopPropagation();
    setRules((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, status: r.status === 'active' ? 'disabled' : r.status === 'disabled' ? 'active' : r.status }
          : r
      )
    );
  };

  const stats = {
    total: rules.length,
    active: rules.filter((r) => r.status === 'active').length,
    critical: rules.filter((r) => r.severity === 'critical' && r.status === 'active').length,
    disabled: rules.filter((r) => r.status === 'disabled').length,
  };

  return (
    <Box className="dr-page">
      {selectedRule && (
        <RuleModal rule={selectedRule} onClose={() => setSelectedRule(null)} />
      )}

      {/* ── Header ── */}
      <Box className="dr-page__header">
        <Box>
          <Typography className="dr-page__title">Detection Rules</Typography>
          <Typography className="dr-page__subtitle">
            SIEM detection rules that identify and surface security threats in real time
          </Typography>
        </Box>

        <div className="dr-header-stats">
          <div className="dr-hstat">
            <span className="dr-hstat__val">{stats.total}</span>
            <span className="dr-hstat__lbl">Total Rules</span>
          </div>
          <div className="dr-hstat">
            <span className="dr-hstat__val dr-hstat__val--green">{stats.active}</span>
            <span className="dr-hstat__lbl">Active</span>
          </div>
          <div className="dr-hstat">
            <span className="dr-hstat__val dr-hstat__val--red">{stats.critical}</span>
            <span className="dr-hstat__lbl">Critical Active</span>
          </div>
          <div className="dr-hstat">
            <span className="dr-hstat__val dr-hstat__val--muted">{stats.disabled}</span>
            <span className="dr-hstat__lbl">Disabled</span>
          </div>
        </div>
      </Box>

      {/* ── Toolbar ── */}
      <div className="dr-toolbar">
        <div className="dr-search">
          <Search size={14} className="dr-search__icon" />
          <input
            className="dr-search__input"
            placeholder="Search rules, categories, platforms…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="dr-toolbar__right">
          <button
            className={`dr-btn dr-btn--ghost${showFilters ? ' dr-btn--active' : ''}`}
            onClick={() => setShowFilters((v) => !v)}
          >
            <Filter size={14} />
            Filters
            <ChevronDown size={12} style={{ transform: showFilters ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
          </button>
          <button className="dr-btn dr-btn--primary">
            <Plus size={14} />
            New Rule
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      {showFilters && (
        <div className="dr-filter-bar">
          <div className="dr-filter-group">
            <span className="dr-filter-group__label">Category</span>
            <div className="dr-pill-group">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  className={`dr-pill ${catFilter === c ? 'dr-pill--active' : ''}`}
                  onClick={() => setCatFilter(c)}
                >{c}</button>
              ))}
            </div>
          </div>
          <div className="dr-filter-group">
            <span className="dr-filter-group__label">Severity</span>
            <div className="dr-pill-group">
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  className={`dr-pill${sevFilter === s ? ' dr-pill--active' : ''}${s !== 'All' ? ` dr-pill--sev-${s.toLowerCase()}` : ''}`}
                  onClick={() => setSevFilter(s)}
                >{s}</button>
              ))}
            </div>
          </div>
          <div className="dr-filter-group">
            <span className="dr-filter-group__label">Status</span>
            <div className="dr-pill-group">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  className={`dr-pill ${stFilter === s ? 'dr-pill--active' : ''}`}
                  onClick={() => setStFilter(s)}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Results count ── */}
      <div className="dr-results-info">
        <span>Showing <strong>{filtered.length}</strong> of <strong>{rules.length}</strong> rules</span>
        {(search || catFilter !== 'All' || sevFilter !== 'All' || stFilter !== 'All') && (
          <button className="dr-clear-btn" onClick={() => { setSearch(''); setCatFilter('All'); setSevFilter('All'); setStFilter('All'); }}>
            <X size={11} /> Clear filters
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="dr-table-wrap">
        <table className="dr-table">
          <thead>
            <tr>
              <th>Severity</th>
              <th>Rule Name</th>
              <th>Category</th>
              <th>Platform</th>
              <th>Tactic</th>
              <th>Status</th>
              <th>Last Triggered</th>
              <th>Hits</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="dr-table__empty">
                  <ShieldOff size={28} />
                  <p>No rules match your filters</p>
                </td>
              </tr>
            )}
            {filtered.map((rule) => (
              <tr
                key={rule.id}
                className="dr-table__row"
                onClick={() => setSelectedRule(rule)}
              >
                <td><SeverityBadge severity={rule.severity} /></td>
                <td className="dr-table__name">
                  <SeverityIcon severity={rule.severity} />
                  <span>{rule.name}</span>
                </td>
                <td><span className="dr-chip">{rule.category}</span></td>
                <td className="dr-table__platform">{rule.platform}</td>
                <td className="dr-table__tactic">{rule.tactic}</td>
                <td><StatusBadge status={rule.status} /></td>
                <td className="dr-table__time">{formatDate(rule.lastTriggered)}</td>
                <td>
                  <span className={`dr-hits${rule.hitCount > 500 ? ' dr-hits--high' : ''}`}>
                    {rule.hitCount.toLocaleString()}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="dr-table__actions">
                    <button
                      className="dr-btn dr-btn--ghost dr-btn--icon"
                      title="View details"
                      onClick={() => setSelectedRule(rule)}
                    >
                      <Eye size={13} />
                    </button>
                    <button className="dr-btn dr-btn--ghost dr-btn--icon" title="Edit">
                      <Edit3 size={13} />
                    </button>
                    <button
                      className="dr-btn dr-btn--ghost dr-btn--icon"
                      title={rule.status === 'active' ? 'Disable' : 'Enable'}
                      onClick={(e) => rule.status !== 'draft' && toggleStatus(rule.id, e)}
                      style={{ opacity: rule.status === 'draft' ? 0.35 : 1 }}
                    >
                      {rule.status === 'active'
                        ? <ToggleRight size={15} style={{ color: 'var(--status-success)' }} />
                        : <ToggleLeft  size={15} style={{ color: 'var(--text-muted)' }} />
                      }
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Box>
  );
}
